import { useState } from 'react';
import { Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '../hooks/useAuth';
import { authService } from '../services/authService';

interface PasswordChangeModalProps {
    isOpen: boolean;
}

export const PasswordChangeModal = ({ isOpen }: PasswordChangeModalProps) => {
    const { getAccessToken, logoutAndRedirectToLogin } = useAuth();
    const [formData, setFormData] = useState({
        newPassword: '',
        confirmPassword: '',
    });
    const [showPasswords, setShowPasswords] = useState({
        newPassword: false,
        confirmPassword: false,
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isSuccess, setIsSuccess] = useState(false);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);

    if (!isOpen) return null;

    const validatePassword = (password: string): string[] => {
        const errors: string[] = [];
        
        if (password.length < 8) {
            errors.push('Must be at least 8 characters');
        }
        if (!/(?=.*[a-z])/.test(password)) {
            errors.push('Must contain at least one lowercase letter');
        }
        if (!/(?=.*[A-Z])/.test(password)) {
            errors.push('Must contain at least one uppercase letter');
        }
        if (!/(?=.*\d)/.test(password)) {
            errors.push('Must contain at least one number');
        }
        if (!/(?=.*[@$!%*?&])/.test(password)) {
            errors.push('Must contain at least one special character (@$!%*?&)');
        }
        
        return errors;
    };

    const handleInputChange = (field: 'newPassword' | 'confirmPassword', value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setError(null);
        
        if (field === 'newPassword') {
            const errors = validatePassword(value);
            setValidationErrors(errors);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        const passwordErrors = validatePassword(formData.newPassword);
        if (passwordErrors.length > 0) {
            setValidationErrors(passwordErrors);
            return;
        }
        
        if (formData.newPassword !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }
        
        setIsLoading(true);
        setError(null);
        
        try {
            const token = await getAccessToken();
            if (!token) {
                throw new Error('Could not obtain access token');
            }
            
            await authService.changePassword(token, formData.newPassword);
            setIsSuccess(true);
            setError(null);
            
            
            setTimeout(async () => {
                try {
                    
                    await logoutAndRedirectToLogin();
                } catch (logoutError) {
                    console.error('[PasswordChangeModal] Auth0 logout failed, forcing manual redirect:', logoutError);
                    
                    window.location.href = '/';
                }
            }, 1500);
        } catch (error) {
            console.error('[PasswordChangeModal] Error:', error);
            setError(error instanceof Error ? error.message : 'Unexpected error changing password');
        } finally {
            setIsLoading(false);
        }
    };

    const togglePasswordVisibility = (field: 'newPassword' | 'confirmPassword') => {
        setShowPasswords(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    return (
        // Mandatory step, not a dismissible modal: no close button, and Escape /
        // clicking outside are explicitly blocked below so it can't be bypassed.
        <Dialog open={isOpen}>
            <DialogContent
                showCloseButton={false}
                onEscapeKeyDown={(e) => e.preventDefault()}
                onInteractOutside={(e) => e.preventDefault()}
                className="sm:max-w-md"
            >
                <div className="text-center mb-2">
                    <div className="mx-auto mb-6">
                        <img
                            src="./PhytoBot_Original.png"
                            alt="Phytobiotics Logo"
                            className="h-16 mx-auto"
                        />
                    </div>
                    <DialogTitle className="text-2xl font-bold text-gray-800 mb-2">
                        Welcome
                    </DialogTitle>
                    <p className="text-sm text-gray-600 mb-4">
                        You need to change your password before continuing with Dr. Phyto.
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                            New Password
                        </label>
                        <div className="relative">
                            <input
                                id="newPassword"
                                type={showPasswords.newPassword ? 'text' : 'password'}
                                value={formData.newPassword}
                                onChange={(e) => handleInputChange('newPassword', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent pr-10"
                                placeholder="Enter your new password"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => togglePasswordVisibility('newPassword')}
                                className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                            >
                                {showPasswords.newPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                        
                        {formData.newPassword && (
                            <div className="mt-2 space-y-1">
                                {validationErrors.map((error, index) => (
                                    <div key={index} className="flex items-center text-xs text-red-600">
                                        <AlertCircle className="w-3 h-3 mr-1" />
                                        {error}
                                    </div>
                                ))}
                                {validationErrors.length === 0 && (
                                    <div className="flex items-center text-xs text-green-600">
                                        <CheckCircle className="w-3 h-3 mr-1" />
                                        Valid password
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div>
                        <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                            Confirm Password
                        </label>
                        <div className="relative">
                            <input
                                id="confirmPassword"
                                type={showPasswords.confirmPassword ? 'text' : 'password'}
                                value={formData.confirmPassword}
                                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent pr-10"
                                placeholder="Confirm your new password"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => togglePasswordVisibility('confirmPassword')}
                                className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                            >
                                {showPasswords.confirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                    </div>

                    {error && !isSuccess && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <div className="flex items-center text-red-800 text-sm">
                                <AlertCircle className="w-4 h-4 mr-2" />
                                {error}
                            </div>
                        </div>
                    )}

                    {isSuccess && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                            <div className="flex items-center text-green-800 text-sm">
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Password changed successfully! Logging out and redirecting...
                            </div>
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={isLoading || isSuccess || validationErrors.length > 0 || !formData.newPassword || !formData.confirmPassword}
                        className="w-full bg-orange-500 text-white py-3 px-4 rounded-lg hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-medium"
                    >
                        {isLoading ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                                Changing...
                            </>
                        ) : isSuccess ? (
                            <>
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Success! Redirecting...
                            </>
                        ) : (
                            'Continue'
                        )}
                    </button>
                </form>

                <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <p className="text-xs text-orange-800">
                        <strong>Password requirements:</strong> Minimum 8 characters, at least one uppercase letter, one lowercase letter, one number, and one special character.
                    </p>
                </div>
            </DialogContent>
        </Dialog>
    );
};
